import {
  pgTable,
  text,
  serial,
  integer,
  boolean,
  timestamp,
  jsonb,
  index,
  varchar,
  uniqueIndex,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Encrypted-at-rest envelope written by server/integrations/payroll/credentialStore.ts.
// Plain Record shape is the legacy pre-encryption format, still readable.
export type SealedConfig = { v: 1; enc: string } | Record<string, string>;

// ── Vendor catalog (single source of truth across payroll + TPA) ────────────
// Each row represents one platform a client can connect to. Categories
// distinguish payroll vendors from TPAs (COBRA, FSA, HSA, HRA).
export const integrationProviders = pgTable(
  "integration_providers",
  {
    id: serial("id").primaryKey(),
    name: text("name").notNull(),
    slug: text("slug").notNull().unique(), // e.g. "adp-run", "wex"
    category: text("category").notNull(),  // payroll | cobra | fsa | hsa | hra
    description: text("description"),
    website: text("website"),
    logoColor: text("logo_color").default("#6366f1"),
    logoInitials: text("logo_initials"),   // fallback avatar when no logo image
    supportedSyncMethods: text("supported_sync_methods").array().default(["api"]), // api | file | manual | sftp
    setupComplexity: text("setup_complexity").default("moderate"), // simple | moderate | complex
    configFields: jsonb("config_fields"),  // schema of required fields for setup
    docUrl: text("doc_url"),
    isActive: boolean("is_active").default(true).notNull(),
    sortOrder: integer("sort_order").default(0).notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (t) => ({
    categoryIdx: index("idx_integration_providers_category").on(t.category),
    activeIdx: index("idx_integration_providers_active").on(t.isActive),
  })
);

export const insertIntegrationProviderSchema = createInsertSchema(integrationProviders)
  .omit({ id: true, createdAt: true, updatedAt: true });

export type IntegrationProvider = typeof integrationProviders.$inferSelect;
export type InsertIntegrationProvider = z.infer<typeof insertIntegrationProviderSchema>;

// ── Platform-level vendor config (payroll) ──────────────────────────────────
// Administr configures these once per vendor: OAuth client_id/secret, ADP
// certs, etc. Encrypted at rest in configData JSONB. The configFields shape
// per vendor lives on the integration_providers row.
export const payrollVendorPlatformConfigs = pgTable(
  "payroll_vendor_platform_configs",
  {
    id: serial("id").primaryKey(),
    vendorKey: text("vendor_key").notNull().unique(), // matches integration_providers.slug for payroll category
    isEnabled: boolean("is_enabled").notNull().default(false),
    configData: jsonb("config_data").$type<SealedConfig>().notNull().default({}),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  }
);

export type PayrollVendorPlatformConfig = typeof payrollVendorPlatformConfigs.$inferSelect;

// ── Platform-level TPA config ───────────────────────────────────────────────
export const tpaProviderPlatformConfigs = pgTable(
  "tpa_provider_platform_configs",
  {
    id: serial("id").primaryKey(),
    providerId: integer("provider_id").notNull().unique(), // FK → integration_providers.id
    isEnabled: boolean("is_enabled").notNull().default(false),
    // oauth2 | api_key | sftp | edi_834 | manual
    authFlow: text("auth_flow").notNull().default("manual"),
    // none | direct | partner | preferred_partner
    partnershipStatus: text("partnership_status").notNull().default("none"),
    configData: jsonb("config_data").$type<Record<string, string>>().notNull().default({}),
    employerSetupInstructions: text("employer_setup_instructions"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  }
);

export type TpaProviderPlatformConfig = typeof tpaProviderPlatformConfigs.$inferSelect;

/** Line-of-coverage → owned vendor company-benefit map, per connection. */
export type PayrollBenefitMap = Record<
  string,
  { uuid: string; vendorBenefitTypeId?: number; description?: string; createdAt: string }
>;

// ── Per-client payroll connection ───────────────────────────────────────────
// One row per (client × vendor). status flow:
//   pending → connecting → connected → (syncing/idle) → error / disconnected
// Credentials JSONB is encrypted-at-rest by the credential store wrapper, not
// at the Drizzle layer — this column holds whatever the adapter needs (OAuth
// tokens, API keys, SFTP host/user/key paths, etc.).
export const clientPayrollConnections = pgTable(
  "client_payroll_connections",
  {
    id: serial("id").primaryKey(),
    clientId: integer("client_id").notNull(),
    vendorKey: text("vendor_key").notNull(),
    status: varchar("status", { length: 20 }).notNull().default("pending"),
    credentials: jsonb("credentials"),
    // Vendor-side employer identifier (e.g. Gusto company_uuid), discovered
    // during finishOAuth
    vendorEmployerId: text("vendor_employer_id"),
    // Per-line vendor benefit ownership (PayrollBenefitMap). Only benefits
    // recorded here are ever written during deduction pushes. Survives
    // disconnect (credentials are nulled; this isn't a secret).
    benefitMap: jsonb("benefit_map").$type<PayrollBenefitMap>(),
    // In-flight OAuth authorize handoff. Single-use: set by oauth/start,
    // cleared by the callback. One in-flight authorize per connection.
    oauthState: varchar("oauth_state", { length: 128 }),
    oauthStateExpiresAt: timestamp("oauth_state_expires_at"),
    oauthReturnTo: text("oauth_return_to"),
    connectedByUserId: varchar("connected_by_user_id"),
    lastSyncedAt: timestamp("last_synced_at"),
    lastError: text("last_error"),
    syncFrequencyHours: integer("sync_frequency_hours").default(24),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (t) => ({
    clientIdx: index("idx_client_payroll_connections_client").on(t.clientId),
    vendorIdx: index("idx_client_payroll_connections_vendor").on(t.vendorKey),
    statusIdx: index("idx_client_payroll_connections_status").on(t.status),
    uniqClient: uniqueIndex("idx_client_payroll_connections_unique").on(t.clientId, t.vendorKey),
    oauthStateIdx: index("idx_client_payroll_connections_oauth_state").on(t.oauthState),
  })
);

export type ClientPayrollConnection = typeof clientPayrollConnections.$inferSelect;

// ── Employee census links (staging + persistent vendor↔Administr mapping) ────
// One row per (connection × vendor employee). The canonical snapshot refreshes
// on every sync; match decisions (matchStatus, employeeId) are only written by
// the auto-matcher or explicit user review actions and survive re-syncs.
// matchStatus: unmatched | needs_review | auto_matched | linked | ignored
//   (auto_matched = machine-linked; linked = human-confirmed or human-created)
export const payrollEmployeeLinks = pgTable(
  "payroll_employee_links",
  {
    id: serial("id").primaryKey(),
    connectionId: integer("connection_id").notNull(), // → client_payroll_connections.id
    clientId: integer("client_id").notNull(),         // denormalized for tenant scoping
    vendorEmployeeId: text("vendor_employee_id").notNull(), // e.g. Gusto employee uuid
    employeeId: integer("employee_id"),               // → employees.id once linked
    matchStatus: varchar("match_status", { length: 20 }).notNull().default("unmatched"),
    matchScore: integer("match_score"),
    matchDetails: jsonb("match_details"),             // { email: boolean, dob: boolean, name: boolean }
    candidates: jsonb("candidates"),                  // [{ employeeId, score, reasons }] for the review UI
    canonical: jsonb("canonical").notNull(),          // CanonicalEmployee snapshot
    firstSeenAt: timestamp("first_seen_at").defaultNow().notNull(),
    lastSyncedAt: timestamp("last_synced_at").defaultNow().notNull(),
    linkedAt: timestamp("linked_at"),
    linkedByUserId: varchar("linked_by_user_id"),     // null when auto_matched
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (t) => ({
    uniqVendorEmp: uniqueIndex("idx_pel_conn_vendor_unique").on(t.connectionId, t.vendorEmployeeId),
    // One Administr employee can be linked to at most one vendor record per
    // connection (rehires with a new vendor uuid must unlink/ignore first)
    uniqLinkedEmp: uniqueIndex("idx_pel_conn_employee_unique")
      .on(t.connectionId, t.employeeId)
      .where(sql`${t.employeeId} IS NOT NULL`),
    clientStatusIdx: index("idx_pel_client_status").on(t.clientId, t.matchStatus),
    employeeIdx: index("idx_pel_employee").on(t.employeeId),
  })
);

export type PayrollEmployeeLink = typeof payrollEmployeeLinks.$inferSelect;
export type PayrollEmployeeLinkMatchStatus =
  | "unmatched"
  | "needs_review"
  | "auto_matched"
  | "linked"
  | "ignored";

// ── Sync runs (one row per orchestrator invocation) ──────────────────────────
// kind: employees | deductions | pay_periods | full. Created in "running" by
// the orchestrator, transitioned to completed/failed when the adapter finishes.
export const payrollSyncRuns = pgTable(
  "payroll_sync_runs",
  {
    id: serial("id").primaryKey(),
    connectionId: integer("connection_id").notNull(),
    kind: varchar("kind", { length: 40 }).notNull(),
    status: varchar("status", { length: 20 }).notNull().default("running"),
    startedAt: timestamp("started_at").defaultNow().notNull(),
    completedAt: timestamp("completed_at"),
    recordsCreated: integer("records_created").default(0),
    recordsUpdated: integer("records_updated").default(0),
    recordsFailed: integer("records_failed").default(0),
    error: text("error"),
    metadata: jsonb("metadata"),
  },
  (t) => ({
    connectionIdx: index("idx_payroll_sync_runs_connection").on(t.connectionId),
    startedIdx: index("idx_payroll_sync_runs_started").on(t.startedAt),
  })
);

export type PayrollSyncRun = typeof payrollSyncRuns.$inferSelect;
